import React, { useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { SalvButton, SalvModal } from '@salvapp/ui-components';
import { scenarioInternalApi, snoozeInternalApi } from '../../apis';
import { useDispatch, useSelector } from 'react-redux';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppState } from '../../store/store';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { DatePicker, Dropdown, Input } from '@salvapp/ui-components';
import * as Yup from 'yup';
import { object } from 'yup';
import { Dayjs } from 'dayjs';
import './snoozeModal.scss';
import { ErrorMessage, Form, Formik, FormikErrors, FormikHelpers, FormikProps, FormikTouched, getIn } from 'formik';
import { getUserId } from '../../auth/authReducer';
import { fetchNotes, fetchPerson } from '../../aml/amlActions';
import { parseErrorMessage } from '../errors/helpers';
import {
  InternalApiScenarioBasicDetails,
  InternalApiScenarioStatus,
  InternalApiScenarioTypeV2,
  InternalApiSortDirection,
} from '../../api-internal';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';

interface Props {
  personId: string;
  dropdownItem?: boolean;
  // Is called when modal is closed.
  // Use this if parent component controls opening the modal.
  modalClosedCallback?: () => void;
}

interface Snooze {
  label: string;
  scenarioHandle: string;
  endDate: Dayjs | null;
  comment: string | undefined;
  limit: number | undefined;
}

interface SnoozesForm {
  snoozes: Snooze[];
}

const validationSchema = Yup.object().shape({
  snoozes: Yup.array().of(
    object({
      endDate: Yup.date()
        .nullable()
        .min(new Date(), 'End date must be in the future')
        .required('Specify when snooze ends'),
      limit: Yup.number().typeError('Limit must be a number').min(1, 'Limit must be positive number').optional(),
      comment: Yup.string().required('Comment is required'),
    })
  ),
});

const SnoozeModal = ({ dropdownItem, personId, modalClosedCallback }: Props) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(modalClosedCallback != undefined);
  const [genericError, setGenericError] = useState<string>();
  const [selectedScenario, setSelectedScenario] = useState<DropdownOption>();
  const [newSnoozes, setNewSnoozes] = useState<Snooze[]>([]);
  const userId = useSelector((state: AppState) => getUserId(state));
  const person = useSelector((state: AppState) => state.aml.person);

  const fetchAllScenarios = async () => {
    let page = 0;
    const pageSize = 50;

    let hasNextPage = true;
    let allScenarios: InternalApiScenarioBasicDetails[] = [];

    while (hasNextPage) {
      const scenariosPage = await scenarioInternalApi.findAllScenarios({
        page,
        size: pageSize,
        types: Object.keys(InternalApiScenarioTypeV2) as InternalApiScenarioTypeV2[],
        statuses: [InternalApiScenarioStatus.ACTIVE, InternalApiScenarioStatus.INACTIVE],
        orderByStatus: InternalApiSortDirection.DESC,
        orderByUpdatedTime: InternalApiSortDirection.DESC,
      });
      allScenarios = allScenarios.concat(scenariosPage.content);

      hasNextPage = scenariosPage.total > allScenarios.length && scenariosPage.size != 0;
      page += 1;
    }

    return allScenarios;
  };

  const { currentOrganisation } = useMe();

  const { data: allScenarios, isFetching } = useQuery({
    queryKey: ['allScenarios', currentOrganisation?.id],
    queryFn: () => fetchAllScenarios(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const reset = () => {
    setGenericError(undefined);
    setShowModal(false);
    setSelectedScenario(undefined);
    setNewSnoozes([]);
    if (modalClosedCallback) modalClosedCallback();
  };

  const formikValueToSnoozes = (formikSnoozes: Snooze[]) =>
    formikSnoozes.map((fv) => {
      return {
        label: fv.label,
        scenarioHandle: fv.scenarioHandle,
        comment: fv.comment,
        endDate: fv.endDate,
        limit: fv.limit,
      };
    });

  const scenarioSelected = (value: DropdownOption, formikValues: SnoozesForm) => {
    setGenericError(undefined);

    const selectedScenarioHandle = value.key;
    const alreadySnoozed = person?.scenarioSnoozeConfigurations?.find((s) => {
      return s.scenarioHandle === selectedScenarioHandle;
    });
    if (alreadySnoozed) {
      setGenericError(`Scenario ${value.label} is already snoozed. It cannot be snoozed again.`);
      return;
    }

    const snoozesInFormik = formikValueToSnoozes(formikValues.snoozes);

    const alreadyChosen = snoozesInFormik.find((s) => {
      return s.scenarioHandle === selectedScenarioHandle;
    });
    if (alreadyChosen) {
      setGenericError(`Scenario ${value.label} is already chosen. It cannot be selected twice.`);
      return;
    }

    setNewSnoozes(
      snoozesInFormik.concat({
        label: value.label,
        scenarioHandle: value.key,
        comment: undefined,
        endDate: null,
        limit: undefined,
      })
    );
  };

  const removeSelectedScenario = (scenarioHandle: string, formikValues: SnoozesForm) => {
    setGenericError(undefined);
    const formikSnoozes = formikValueToSnoozes(formikValues.snoozes);
    setNewSnoozes(formikSnoozes.filter((s) => s.scenarioHandle !== scenarioHandle));
  };

  const submit = async (values: SnoozesForm, { setSubmitting }: FormikHelpers<SnoozesForm>) => {
    setGenericError(undefined);

    try {
      await snoozeInternalApi.addSnoozeConfigurations({
        personId: personId,
        internalApiSnoozeConfig: values.snoozes.map((s) => ({
          createdBy: { id: userId! },
          endTime: s.endDate!.toDate(),
          note: s.comment!,
          scenarioHandle: s.scenarioHandle,
          totalAmount: s.limit,
        })),
      });

      await dispatch(fetchPerson(personId));
      await dispatch(fetchNotes(personId));
      reset();
    } catch (e) {
      const message = await parseErrorMessage(e);
      setGenericError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderNewSnoozes = (
    values: SnoozesForm,
    isSubmitting: boolean,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void,
    isValid: boolean,
    errors: FormikErrors<SnoozesForm>,
    touched: FormikTouched<SnoozesForm>
  ) => {
    const snoozeJSXList = [];
    for (let i = 0; i < values.snoozes.length; i++) {
      const snooze = values.snoozes[i];
      const keyEndDate = `snoozes[${i}].endDate`;
      const keyLimit = `snoozes[${i}].limit`;
      const keyComment = `snoozes[${i}].comment`;
      const commentIsInvalid = getIn(errors, keyComment) && getIn(touched, keyComment);
      const limitIsInvalid = getIn(errors, keyLimit) && getIn(touched, keyLimit);
      const endDateIsInvalid = getIn(errors, keyEndDate) && getIn(touched, keyEndDate);

      snoozeJSXList.push(
        <Card key={snooze.scenarioHandle}>
          <Card.Body>
            <Container>
              <Row>
                <Col data-testid={`snooze-item-${snooze.scenarioHandle}`} xs={11}>
                  <h3>{snooze.label}</h3>
                </Col>
                <Col>
                  <Button
                    data-testid={`close-item-${snooze.scenarioHandle}`}
                    variant='outline-secondary'
                    onClick={() => removeSelectedScenario(snooze.scenarioHandle, values)}
                  >
                    <FontAwesomeIcon className='caps-l' icon={faXmark} />
                  </Button>
                </Col>
              </Row>
              <Row className='p-t-16'>
                <Col>
                  <h3>End date</h3>
                </Col>
                <Col>
                  <h3>Transactions sum limit</h3>
                </Col>
              </Row>
              <Row>
                <Col>
                  <DatePicker
                    showDefaultOptions={false}
                    value={snooze.endDate}
                    showTime={false}
                    onChange={(newDate) => {
                      setFieldValue(keyEndDate, newDate);
                    }}
                    isInvalid={endDateIsInvalid}
                  />
                  <ErrorMessage
                    name={keyEndDate}
                    render={(message) => {
                      return (
                        <div data-testid={`error-date-${snooze.scenarioHandle}`} className='error-msg'>
                          {message}
                        </div>
                      );
                    }}
                  />
                </Col>
                <Col>
                  <Input
                    name={`limit${i}`}
                    placeholder='Enter sum'
                    value={snooze.limit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue(keyLimit, e.target.value);
                    }}
                    isInvalid={limitIsInvalid}
                  />
                  <ErrorMessage
                    name={keyLimit}
                    render={(message) => (
                      <div data-testid={`error-limit-${snooze.scenarioHandle}`} className='error-msg'>
                        {message}
                      </div>
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col className='explanation text-s m-y-12'>
                  “Transactions sum limit” is the total of this person’s incoming and outgoing payments calculated from
                  the start of the snoozing. When either the “End date” or the “Transactions sum limit” is reached, then
                  this snooze will be ended automatically.
                </Col>
              </Row>
              <Row>
                <Col>
                  <h3>Comment</h3>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    name={`comment${i}`}
                    placeholder='Enter reason for snoozing'
                    value={snooze.comment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue(keyComment, e.target.value);
                    }}
                    isInvalid={commentIsInvalid}
                  />
                  <ErrorMessage
                    name={keyComment}
                    render={(message) => (
                      <div data-testid={`error-comment-${snooze.scenarioHandle}`} className='error-msg'>
                        {message}
                      </div>
                    )}
                  />
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
      );
    }
    return <>{snoozeJSXList}</>;
  };

  return (
    <>
      {!modalClosedCallback && dropdownItem && (
        <Button
          onClick={() => {
            setShowModal(true);
          }}
          className='dropdown-item'
        >
          Snooze scenario
        </Button>
      )}
      {showModal && (
        <Formik
          initialValues={{
            snoozes: newSnoozes,
          }}
          onSubmit={submit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {({
            values,
            isSubmitting,
            setFieldValue,
            isValid,
            errors,
            submitForm,
            touched,
          }: FormikProps<SnoozesForm>) => {
            return (
              <Form>
                <SalvModal show={showModal} onHide={() => reset()} dialogClassName='snooze-scenario-modal'>
                  <SalvModal.Header closeButton>Snooze scenario</SalvModal.Header>

                  <SalvModal.Body>
                    <>
                      {!isFetching && (
                        <div>
                          <Dropdown
                            name='scenario'
                            label='Find scenarios'
                            placeholder='Search and select scenarios'
                            options={allScenarios!.map((scenario): DropdownOption => {
                              return {
                                key: scenario.id!,
                                value: scenario.id!,
                                label: scenario.name,
                              };
                            })}
                            value={selectedScenario}
                            onChange={(value: DropdownOption[]) => {
                              scenarioSelected(value[0], values);
                              setSelectedScenario(value[0]);
                            }}
                          />
                          <h3 className='p-t-12'>Selected scenarios</h3>
                          {renderNewSnoozes(values, isSubmitting, setFieldValue, isValid, errors, touched)}
                          {genericError && (
                            <div data-testid='generic-error' className='error-msg'>
                              {genericError}
                            </div>
                          )}
                        </div>
                      )}
                      {isFetching && <div>Loading...</div>}
                    </>
                  </SalvModal.Body>

                  <SalvModal.Footer>
                    <SalvButton
                      label='Cancel'
                      variant='muted'
                      onClick={() => {
                        reset();
                      }}
                    />
                    <SalvButton
                      disabled={isSubmitting}
                      type='submit'
                      label='Save'
                      variant='primary'
                      onClick={submitForm}
                    />
                  </SalvModal.Footer>
                </SalvModal>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

export default SnoozeModal;
